import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

export type Role = 'super_admin' | 'admin' | 'editor';
export type UserStatus = 'active' | 'inactive';

export interface Profile {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: UserStatus;
  last_login: string | null;
  created_at: string;
}

export interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  image_url: string;
  cta_text: string;
  cta_link: string;
  display_order: number;
  created_at: string;
}

export interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string;
  external_url: string | null;
  event_date: string | null;
  category: 'news' | 'event' | 'announcement';
  status: 'draft' | 'published';
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface GalleryImage {
  id: string;
  title: string;
  image_url: string;
  event_name: string;
  date_uploaded: string;
}

export interface Stat {
  id: string;
  label: string;
  value: number;
  display_order: number;
}

export interface SiteSetting {
  id: string;
  key: string;
  value: string;
}

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  created_at: string;
}

export interface NewsletterSubscriber {
  id: string;
  email: string;
  created_at: string;
}

export async function uploadImage(file: File, folder: string = 'general'): Promise<string | null> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  const path = `${folder}/${timestamp}-${random}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('images')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Upload error:', uploadError);
    return null;
  }

  const { data } = supabase.storage.from('images').getPublicUrl(path);
  return data.publicUrl;
}

export async function deleteImage(url: string): Promise<boolean> {
  const bucketName = 'images';
  const bucketUrl = supabase.storage.from(bucketName).getPublicUrl('').data.publicUrl;
  const path = url.replace(bucketUrl, '');
  if (!path || path === url) return false;

  const { error } = await supabase.storage.from(bucketName).remove([path]);
  return !error;
}